import {Injectable, OnDestroy} from "@angular/core";
import {Observable, Subject, Subscription} from "rxjs";
import {SessionModel} from "../orchestration/session.model";
import {
    MenuCompositeData,
    MenuData,
    MenuExtraGroupData,
    MenuExtraProductData,
    MenuItem,
    MenuItemData,
    MenuPlgData,
    MenuPlgSellableData,
    MenuProductLinePlgQtyData,
    MenuProductLineQtyData,
    MenuQtyData,
    MenuSellableData,
} from "../menu/menu-entity.model";
import {DataClientErrorMessage, EntityData} from "@fedelta-pos/data-client";
import {NotificationService} from "../notification/notification.service";
import {ResponsiveService} from "../orchestration/responsive.service";
import {AnalyticsService, OrderPlacedEvent} from "../orchestration/analytics.service";
import {DateTime} from "../string-utils/date-time";
import {OrderingContextModel} from "../ordering-context/ordering-context.model";
import {MessageDetails, MessageLevel} from "../notification/message/message";
import {PaymentModel} from "../payment/payment.model";
import {DataServerConditionNotification} from "@fedelta-pos/data-client/error/error.service";
import {LoadingService} from "../ui-elements/loading/loading.service";
import {NavigationService} from "../orchestration/navigation.service";
import {OrchestrationEvents} from "../orchestration/orchestration-events";
import {MenuEvents} from "../menu/menu-events";
import {GroupOrderEvents} from "../group-order/group-order-events";
import {DealEvents} from "../deal/deal-events";
import {
    OrderEvents,
    OrderInitiationStatus,
    OrderSubmissionStatus,
    UnavailableOrderItemRemovalRequest
} from "./order-events";
import {PossessivePipe} from "../string-utils/possessive.pipe";
import {OrderingPhase, SessionEvents} from "../orchestration/session-events";
import {IdentityModel} from "../identity/identity.model";
import {PendingOrderData, PendingOrdersData, PendingOrdersService} from "./pending-orders.service";
import {
    CustomerDetails,
    CustomerDetailsService,
    PaymentCustomerDetails
} from "../customer-info/customer-details.service";
import {ActiveSchedulesSearchModel} from "../schedule/active-schedules-search.model";
import {
    AvailableOrderTimesRequestData,
    AvailableOrderTimesResponseData,
    AvailableOrderTimesService
} from "./available-order-times.service";
import {AppUserService} from "../identity/app-user.service";
import {OrderPostscriptData, OrderPostscriptService} from "./order-postscript.service";
import {
    ExtraItemData,
    InitiatedOrderData,
    InitiatedOrderItemData,
    OrderInitiationData,
    OrderInitiationService,
    OrderItemData,
    PaymentSurchargeData
} from "./order-initiation.service";
import {
    OrderItemSchedulesData,
    OrderPlacementData,
    OrderPlacementService,
    OrderPlacementStatus,
    PaymentAuthorisationData,
    PlacedOrderData,
    PlacedOrdersData
} from "./order-placement.service";
import {OrderPlacementStatusData, OrderPlacementStatusService} from "./order-placement-status.service";
import {RecentOrderData, RecentOrderService} from "./recent-order.service";
import {
    OrderTransferRequestPollService,
    OrderTransferRequestsData,
    OrderTransferTargetStatus
} from "../group-order/order-transfer-request-poll.service";
import {OrderTransferService} from "../group-order/order-transfer.service";
import {EntitlementStatus} from "../deal/entitlement-entity.model";
import {UUID} from "angular2-uuid";
import {IntegratedPaymentProvider, PaymentCard, PaymentType, ThreeDSResult} from "../payment/payment";
import {OrderTransferTargetReferenceData} from "../group-order/order-transfer-target-entity.model";
import {DisplayableOrderItem, OrderCompositeItem, OrderExtraItem, OrderItem, OrderPlgItem, OrderType} from "./order";
import {MenuModel} from "../menu/menu.model";
import {DealModel} from "../deal/deal.model";
import {GroupOrderModel} from "../group-order/group-order.model";

export class OrderErrorStates {
    public orderWithInvalidTimeAttempted: boolean = false;
    public tableOrderWithTimeAttempted: boolean = false;
    public emptyOrderAttempted: boolean = false;
    public minMaxOrderAmountAttempted: boolean = false;

    public get hasErrors(): boolean {
        return this.emptyOrderAttempted ||
            this.orderWithInvalidTimeAttempted ||
            this.minMaxOrderAmountAttempted;
    }

    public clearOrderingContextErrors(): void {
        this.orderWithInvalidTimeAttempted = false;
        this.tableOrderWithTimeAttempted = false;
    }

    public clearOrderLimitErrors(): void {
        this.emptyOrderAttempted = false;
        this.minMaxOrderAmountAttempted = false;
    }
}

export enum OrderErrorCode {
    ORDER_NOT_INITIATED,
    ORDER_TOKEN_MISSING
}

export class OrderError {
    constructor(public code: OrderErrorCode, public message: string) {
    }
}

export class TransferRequest {
    items: OrderItem[];
    subTotal: number;

    constructor(public transferRequestId: string, public transferRequestName: string) {
    }
}

@Injectable()
export class OrderModel implements OnDestroy {
    private static readonly ORDER_INITIATION_ATTEMPTS_LIMIT: number = 3;
    private static readonly ORDER_PLACEMENT_DELAY_TRIGGER_SECONDS: number = 3;

    public static readonly SHOW_RELATIVE_PRICING_FOR_SIZES: boolean = false;
    public static readonly SHOW_RELATIVE_PRICING_FOR_EXTRAS: boolean = false;
    public static readonly SHOW_RELATIVE_PRICING_FOR_EXTRA_SIZES: boolean = false;

    private static readonly REORDER_ROUND_CUTOFF_HOURS: number = 8;

    private subscriptions: Subscription = new Subscription();

    private orderItems: OrderItem[] = [];
    public tipAmount: number = 0;
    public orderErrorStates: OrderErrorStates = new OrderErrorStates();
    private isOrderValid: boolean = false;
    private hasOrderBeenPersisted: boolean = false;

    private orderInitiationRequested$: Subject<void> = new Subject<void>();
    private orderInitiationDebouncer: Observable<void> = this.orderInitiationRequested$.debounceTime(500);
    public initiationStatus: OrderInitiationStatus = OrderInitiationStatus.UNINITIATED;
    private initiationSequence: number = 0;
    public orderInitiatedTime: number = 0;
    private initiationError: DataClientErrorMessage;
    private orderInitiation: OrderInitiationData = <OrderInitiationData>EntityData.create(OrderInitiationData);
    public initiatedOrder: InitiatedOrderData = <InitiatedOrderData>EntityData.create(InitiatedOrderData);

    private orderPlacementStarted: number;
    public orderPlacementFinished: number;
    private orderPlacement: OrderPlacementData;
    public placedOrder: PlacedOrderData;

    private _canReorderOnLoad: boolean = true;
    private _canRestoreOnLoad: boolean = true;

    private removalOfOversoldItemsReported: boolean = false;

    public transferRequests: TransferRequest[] = [];
    public totalTransfers: number = 0;

    private verifyingPendingOrders: boolean = false;
    private verifiedOrders: RecentOrderData[] = [];

    // Avoid injection dependency on GroupOrderModel
    private _groupOrderModel: GroupOrderModel;

    constructor(private sessionModel: SessionModel,
                private identityModel: IdentityModel,
                private orderingContextModel: OrderingContextModel,
                private pendingOrdersService: PendingOrdersService,
                private menuModel: MenuModel,
                private dealModel: DealModel,
                private paymentModel: PaymentModel,
                private recentOrderService: RecentOrderService,
                private orderInitiationService: OrderInitiationService,
                private orderPlacementService: OrderPlacementService,
                private orderPlacementStatusService: OrderPlacementStatusService,
                private orderPostscriptService: OrderPostscriptService,
                private orderTransferService: OrderTransferService,
                private orderTransferRequestPollService: OrderTransferRequestPollService,
                private availableOrderTimesService: AvailableOrderTimesService,
                private activeSchedulesSearchModel: ActiveSchedulesSearchModel,
                private customerDetailsService: CustomerDetailsService,
                private appUserService: AppUserService,
                private notificationService: NotificationService,
                private navigationService: NavigationService,
                private loadingService: LoadingService,
                private analyticsService: AnalyticsService,
                private responsiveService: ResponsiveService) {
        console.log("--- OrderModel");

        OrderEvents.orderModel.next(this);

        this.subscriptions.add(OrderEvents.orderChanged$.subscribe(() => {
            this.buildTransferRequests();
            OrderEvents.orderHasItems$.next(this.hasOrderedItems());
            this.orderErrorStates.clearOrderLimitErrors();
            this.requestOrderInitiation("order changed");
        }));
        this.subscriptions.add(this.orderInitiationDebouncer.subscribe(() => this.initiateOrder()));
        this.subscriptions.add(OrchestrationEvents.sessionReset$.subscribe(() => {
            this.reset("session reset");
            this.hasOrderBeenPersisted = false;
        }));
        this.subscriptions.add(MenuEvents.currentMenu.subscribe((menuData: MenuData) => this.menuLoaded(menuData)));
    }

    set groupOrderModel(groupOrderModel: GroupOrderModel) {
        this._groupOrderModel = groupOrderModel;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private startNewOrder(reason: string): void {
        console.log(Date.now(), "starting new order:", reason);
        this.isOrderValid = true;
        OrderEvents.orderChanged$.next();
    }

    public invalidateOrder(): void {
        if (this.dealModel.currentEntitlement) {
            this.dealModel.removeCurrentEntitlement();
        }
        if (this.hasOrderedItems()) {
            this.isOrderValid = false;
            this.orderItems = [];
        }
        OrderEvents.orderChanged$.next();
    }

    public reset(reason: string): void {
        console.log(Date.now(), "resetting order model:", reason);
        this.isOrderValid = false;
        this.orderItems = [];
        this.tipAmount = 0;
        this.notifyOrderInitiationStatus(OrderInitiationStatus.UNINITIATED);
        this.initiationError = undefined;
        this.orderInitiation = <OrderInitiationData>EntityData.create(OrderInitiationData);
        this.initiatedOrder = <InitiatedOrderData>EntityData.create(InitiatedOrderData);
        this.orderPlacement = undefined;
        this.placedOrder = undefined;
        this.orderErrorStates = new OrderErrorStates();
        this.transferRequests = [];
        this.totalTransfers = 0;
        OrderEvents.orderChanged$.next();
    }

    public createOrderItem(menuItem: MenuItemData, quantity: number, orderDefaultExtras: boolean = true): OrderItem {
        if (MenuEvents.currentMenu.value) {
            this.menuModel.initialiseItem(menuItem);
        }
        let orderItem: OrderItem;
        if (menuItem.sellable.composite && menuItem.sellable.composite.sellableId) {
            const compositeOrderItems: OrderItem[] = [];
            const qtyOrderedBySellableId: { [sellableId: number]: number } = {};
            menuItem.sellable.composite.prodLineQuantities.forEach((prodLineQty: MenuProductLineQtyData) => {
                compositeOrderItems[prodLineQty.indexOrder] = new OrderItem(
                    undefined, prodLineQty.productLine.menuItem ? prodLineQty.productLine.menuItem.name : prodLineQty.productLine.name,
                    prodLineQty.productLine.menuItem, prodLineQty.productLine, 1);
                qtyOrderedBySellableId[prodLineQty.productLine.sellableId] = (qtyOrderedBySellableId[prodLineQty.productLine.sellableId] || 0) + 1;
            });
            menuItem.sellable.composite.prodLinePlgQuantities.forEach((prodLinePlgQty: MenuProductLinePlgQtyData) => {
                const defaultPlgSellable: MenuPlgSellableData = prodLinePlgQty.plg.sellableLines.find((plgSellable: MenuPlgSellableData) => plgSellable.defaultSellable);
                let defaultSellable: MenuSellableData = defaultPlgSellable ? defaultPlgSellable.sellable : undefined;

                // Don't set the default if the sellable is already at the available limit
                if (defaultPlgSellable && defaultPlgSellable.sellable) {
                    if (!this.menuModel.plgItemIsAvailable(defaultPlgSellable.sellable.menuItem, menuItem)) {
                        defaultSellable = undefined;
                    } else if (this.menuModel.sellableQtyAvailable(defaultPlgSellable.sellable.sellableId) != undefined) {
                        const qtyOrdered: number = this.sellableQtyOrdered(defaultSellable.sellableId) + (qtyOrderedBySellableId[defaultSellable.sellableId] || 0);
                        if (qtyOrdered >= this.menuModel.sellableQtyAvailable(defaultPlgSellable.sellable.sellableId)) {
                            defaultSellable = undefined;
                        }
                    }
                }

                compositeOrderItems[prodLinePlgQty.indexOrder] = new OrderPlgItem(
                    undefined, defaultSellable ? (defaultSellable.menuItem ? defaultSellable.menuItem.name : defaultSellable.name) : undefined,
                    defaultSellable, 1, prodLinePlgQty.plg);
            });
            const orderCompositeItem: OrderCompositeItem = new OrderCompositeItem(undefined, menuItem.name, menuItem, menuItem.sellable, quantity, menuItem.sellable.composite, compositeOrderItems);
            orderItem = orderCompositeItem;

            // Extras are added last so that available-quantity checks take into account order line quantities.
            if (orderDefaultExtras) {
                orderCompositeItem.items.forEach((compositeOrderItem: OrderItem) => compositeOrderItem.addDefaultExtras(orderCompositeItem));
            }
        } else {
            orderItem = new OrderItem(undefined, menuItem.name, menuItem, menuItem.sellable, quantity);

            if (orderDefaultExtras) {
                orderItem.addDefaultExtras();
            }
        }
        orderItem.dirty = false;
        return orderItem;
    }

    private sellableQtyOrdered(sellableId: number, excludeOrderItem?: OrderItem): number {
        return this.orderItems
            .filter((item: OrderItem) => item !== excludeOrderItem)
            .reduce((total: number, item: OrderItem) => total + item.sellableQtyOrdered(item.quantity, sellableId), 0);
    }

    public accumulateSellableQtyOrdered(pendingItem: OrderItem): { [sellableId: number]: number } {
        const qtyOrderedBySellableId: { [sellableId: number]: number } = {};
        this.orderItems.forEach((item: OrderItem) => item.accumulateSellableQtyOrdered(item.quantity, qtyOrderedBySellableId));
        if (pendingItem) {
            pendingItem.accumulateSellableQtyOrdered(pendingItem.quantity, qtyOrderedBySellableId);
        }
        return qtyOrderedBySellableId;
    }

    public addOrderItem(orderItem: OrderItem, index?: number): void {
        if (!this.isOrderValid) {
            this.startNewOrder("new item");
        }
        orderItem.addToList(this.orderItems, index);
        OrderEvents.orderChanged$.next();
    }

    public addOrderItems(orderItems: OrderItem[]): void {
        if (!this.isOrderValid) {
            this.startNewOrder("new items");
        }
        let changed: boolean = false;
        orderItems.forEach((orderItem: OrderItem) => {
            if (orderItem) {
                orderItem.addToList(this.orderItems);
                changed = true;
            }
        });
        if (changed) {
            OrderEvents.orderChanged$.next();
        }
    }

    public getRecentlyOrderedItems(): OrderItem[] {
        return this.orderItems.filter((item: OrderItem) => item.justOrdered && !item.transferRequestId);
    }

    public async removeOrderItem(orderItem: DisplayableOrderItem): Promise<boolean> {
        if (orderItem.deal) {
            if (!await this.notificationService.displayConfirmation("woa.heading.order.confirmRemoveDeal", undefined,
                "woa.message.order.confirmRemoveItemAndDeal",
                "woa.button.common.noThanks", "woa.button.common.remove", true,
                {
                    item: orderItem.name,
                    deal: this.dealModel.currentEntitlement.appText1 || this.dealModel.currentEntitlement.distributionChannel.distributionReference
                })) {
                return false;
            }
            this.dealModel.removeCurrentEntitlement();

            // noinspection ES6MissingAwait
            this.analyticsService.trackItemRemoved(orderItem);
        } else {
            this.removeOrderItemByIdentifier(orderItem.identifier);

            // noinspection ES6MissingAwait
            this.analyticsService.trackItemRemoved(orderItem);
        }
        return true;
    }

    private removeOrderItemByIdentifier(identifier: string, notify: boolean = true): boolean {
        const index: number = this.orderItems.findIndex((item: OrderItem) => item.identifier === identifier);
        if (index < 0) {
            return false;
        }
        this.orderItems.splice(index, 1);
        if (!this.orderItems.length) {
            this.orderInitiation = <OrderInitiationData>EntityData.create(OrderInitiationData);
            this.initiatedOrder = <InitiatedOrderData>EntityData.create(InitiatedOrderData);
            this.notifyOrderInitiationStatus(OrderInitiationStatus.UNINITIATED);
        }
        if (notify) {
            OrderEvents.orderChanged$.next();
        }
        return true;
    }

    public removeOrderItemsByIdentifier(identifiers: string[]): boolean {
        let removed: boolean = false;
        identifiers.forEach((identifier) => {
            if (this.removeOrderItemByIdentifier(identifier, false)) {
                removed = true;
            }
        });
        OrderEvents.orderChanged$.next();
        return removed;
    }

    public removeTransferRequestOrderItems(predicate?: TransferRequest | ((transferRequestId: string) => boolean)): boolean {
        return this.removeOrderItemsByIdentifier(this.orderItems
            .filter((orderItem: OrderItem) => orderItem.transferRequestId && (!predicate ||
                (predicate instanceof TransferRequest && orderItem.transferRequestId === predicate.transferRequestId) ||
                ((<(transferRequestId: string) => boolean>predicate)(orderItem.transferRequestId))))
            .map(((orderItem: OrderItem) => orderItem.identifier)));
    }

    public replaceOrderItemByIdentifier(identifier: string, orderItem: OrderItem): void {
        const index: number = this.orderItems.findIndex((item: OrderItem) => item.identifier === identifier);
        this.removeOrderItemByIdentifier(identifier, false);
        this.addOrderItem(orderItem, index);
    }

    /**
     * Attempts to restore details of the locally persisted current order.
     * The current order must be empty.
     * Details which are restored include customer contact details, and ordered items.
     * Customer contact details are assumed to be untrusted, and saved customer details will be used in preference if available.
     *
     * Returns the persisted recent order data, so that the caller can restore additional details which may have been provided to initiateOrder().
     */
    public restoreOrder(): RecentOrderData {
        if (this.hasOrderedItems()) {
            return undefined;
        }

        const recentOrder: RecentOrderData = this.recentOrderService.getCurrentOrder();
        if (!recentOrder) {
            return undefined;
        }

        this.orderingContextModel.firstName = recentOrder.orderInitiation.firstName === "NOT ASSIGNED" ? undefined : recentOrder.orderInitiation.firstName;
        this.orderingContextModel.lastName = recentOrder.orderInitiation.lastName === "NOT ASSIGNED" ? undefined : recentOrder.orderInitiation.lastName;
        this.orderingContextModel.email = recentOrder.orderInitiation.email;
        this.orderingContextModel.mobilePhone = recentOrder.orderInitiation.orderPhoneNumber;
        this.orderingContextModel.address = recentOrder.orderInitiation.address;
        this.orderingContextModel.deliveryInstructions = recentOrder.orderInitiation.deliveryInstructions;
        this.orderingContextModel.firstNameTrusted = false;
        this.orderingContextModel.lastNameTrusted = false;
        this.orderingContextModel.emailTrusted = false;
        this.orderingContextModel.mobilePhoneTrusted = false;
        this.orderingContextModel.addressTrusted = false;
        this.orderingContextModel.deliveryInstructionsTrusted = false;

        // Reload any saved customer details and/or reapply member details
        this.orderingContextModel.applyIdentityContactDetails(this.identityModel.currentIdentity);

        if (recentOrder.orderInitiation.items.length) {
            this.addOrderItems(this.recreateOrderItems(recentOrder.orderInitiation.items, true, true, true));
        }

        return recentOrder;
    }

    private recreateOrderItems(items: OrderItemData[], checkQtyAvailable: boolean = true, ignoreProposed: boolean = false, restored: boolean = false): OrderItem[] {
        const runningTotal: { [sellableId: number]: number } = {};
        return items
            .filter((orderItemData: OrderItemData) => !(ignoreProposed && orderItemData.proposed))
            .map((orderItemData: OrderItemData) => [orderItemData, this.menuModel.findMergedItemForSellable(orderItemData.sellableId, this.orderingContextModel.orderType)])
            .filter(([, menuItem]: [OrderItemData, MenuItemData]) => this.menuModel.itemIsAvailable(menuItem))
            .map(([orderItemData, menuItem]: [OrderItemData, MenuItemData]) => orderItemData.compositeOrderItems.length
                ? this.createOrderCompositeItemFromOrderItemData(orderItemData, menuItem, checkQtyAvailable, runningTotal)
                : this.createOrderItemFromOrderItemData(orderItemData, menuItem, undefined, checkQtyAvailable, undefined, runningTotal))
            .filter((orderItem: OrderItem) => !!orderItem)
            .map((orderItem: OrderItem) => {
                if (restored) {
                    // This will prevent the item from being marked as justOrdered when the order initiation completes
                    orderItem.initiatedTotal = 0;
                }
                return orderItem;
            });
    }

    private sellableQtyStillAvailable(sellableId: number, runningTotal: { [sellableId: number]: number }): number {
        return this.menuModel.sellableQtyAvailable(sellableId) - this.sellableQtyOrdered(sellableId) - ((runningTotal && runningTotal[sellableId]) || 0);
    }

    private addExtrasFromOrderItemData(orderItem: OrderItem,
                                       orderItemData: OrderItemData,
                                       checkQtyAvailable: boolean,
                                       parentQty: number,
                                       runningTotal: { [sellableId: number]: number }): boolean {
        return orderItemData.extras.every((extraItemData: ExtraItemData) => {
            const extraGroup: MenuExtraGroupData = orderItem.availableExtraGroups.find((extraData: MenuExtraGroupData) => extraData.name === extraItemData.groupName);
            let changed: boolean = false;
            if (extraGroup) {
                const extraProductData: MenuExtraProductData = extraGroup.extraProducts.find((extraProductData: MenuExtraProductData) => extraProductData.productLine.sellableId === extraItemData.sellableId);
                if (extraProductData) {
                    if (checkQtyAvailable &&
                        extraProductData.productLine && this.menuModel.sellableQtyAvailable(extraProductData.productLine.sellableId) != undefined &&
                        (parentQty || 1) * extraItemData.quantity > this.sellableQtyStillAvailable(extraProductData.productLine.sellableId, runningTotal)) {
                        return false;
                    }
                    orderItem.addOrderExtraItem(orderItem.createOrderExtraItem(extraGroup, extraProductData, extraItemData.quantity));
                    changed = true;
                }
            }
            if (changed) {
                orderItem.itemChanged();
            }
            return true;
        });
    }

    private createOrderItemFromOrderItemData(orderItemData: OrderItemData,
                                             menuItem: MenuItemData,
                                             sellable: MenuSellableData,
                                             checkQtyAvailable: boolean,
                                             parentQty: number,
                                             runningTotal: { [sellableId: number]: number }): OrderItem {
        if (!sellable) {
            sellable = menuItem.sellable;
        }
        const minQty: number = sellable.minWebOrderQty || 1;
        let qtyToOrder: number;
        if (checkQtyAvailable && sellable && this.menuModel.sellableQtyAvailable(sellable.sellableId) != undefined) {
            const qtyAvailable: number = this.sellableQtyStillAvailable(sellable.sellableId, runningTotal);
            if (parentQty != undefined && parentQty * orderItemData.quantity > qtyAvailable) {
                return undefined;
            }
            qtyToOrder = Math.min(Math.max(orderItemData.quantity, minQty), qtyAvailable);
        } else {
            qtyToOrder = Math.max(orderItemData.quantity, minQty);
        }
        if (qtyToOrder < minQty) {
            return undefined;
        }
        const orderItem: OrderItem = new OrderItem(undefined, orderItemData.name, menuItem, sellable, qtyToOrder, orderItemData.transferRequestId, orderItemData.transferRequestName, orderItemData.deal, orderItemData.proposed);
        if (!this.addExtrasFromOrderItemData(orderItem, orderItemData, checkQtyAvailable, (parentQty || 1) * qtyToOrder, runningTotal)) {
            return undefined;
        }
        if (checkQtyAvailable && parentQty == undefined) {
            orderItem.accumulateSellableQtyOrdered((parentQty || 1) * qtyToOrder, runningTotal);
        }
        return orderItem;
    }

    private createOrderPlgItemFromOrderItemData(orderItemData: OrderItemData,
                                                sellable: MenuSellableData,
                                                plg: MenuPlgData,
                                                checkQtyAvailable: boolean,
                                                parentQty: number,
                                                runningTotal: { [sellableId: number]: number }): OrderItem {
        if (checkQtyAvailable && sellable && this.menuModel.sellableQtyAvailable(sellable.sellableId) != undefined && (parentQty || 1) * orderItemData.quantity > this.sellableQtyStillAvailable(sellable.sellableId, runningTotal)) {
            return undefined;
        }
        const orderItem: OrderItem = new OrderPlgItem(undefined, orderItemData.name, sellable, orderItemData.quantity, plg, orderItemData.transferRequestId, orderItemData.transferRequestName);
        if (!this.addExtrasFromOrderItemData(orderItem, orderItemData, checkQtyAvailable, (parentQty || 1) * orderItemData.quantity, runningTotal)) {
            return undefined;
        }
        return orderItem;
    }

    private createOrderCompositeItemFromOrderItemData(orderItemData: OrderItemData,
                                                      menuItem: MenuItemData,
                                                      checkQtyAvailable: boolean = true,
                                                      runningTotal: {
                                                          [sellableId: number]: number
                                                      }): OrderCompositeItem {
        const qtyToOrder: number = checkQtyAvailable && this.menuModel.sellableQtyAvailable(orderItemData.sellableId) != undefined
            ? Math.min(orderItemData.quantity, this.sellableQtyStillAvailable(orderItemData.sellableId, runningTotal))
            : orderItemData.quantity;
        if (qtyToOrder <= 0) {
            return undefined;
        }
        const composite: MenuCompositeData = menuItem.sellable.composite;
        const compositeLines: MenuQtyData[] = this.menuModel.getCompositeLines(composite);
        const items: OrderItem[] = [];
        if (!orderItemData.compositeOrderItems.every((compositeOrderItemData: OrderItemData, i: number) => {

            // We can assume positional correspondence between the composite lines in the OrderItemData and the definition.
            const compositeLine: MenuQtyData = compositeLines[i];

            // If the sellable being added doesn't match the definition, or if it would exceed the available quantity, then the entire composite is abandoned.
            if (this.menuModel.isProductLinePlgQty(compositeLine)) {
                const plg: MenuPlgData = (<MenuProductLinePlgQtyData>compositeLine).plg;
                const plgSellable: MenuPlgSellableData = plg.sellableLines.find((sellableLine: MenuPlgSellableData) => sellableLine.sellable.sellableId === compositeOrderItemData.sellableId);
                if (!plgSellable) {
                    return false;
                }
                const item: OrderItem = this.createOrderPlgItemFromOrderItemData(compositeOrderItemData, plgSellable.sellable, plg, checkQtyAvailable, orderItemData.quantity, runningTotal);
                if (!item) {
                    return false;
                }
                items.push(item);
            } else if (this.menuModel.isProductLineQty(compositeLine)) {
                const sellable: MenuSellableData = (<MenuProductLineQtyData>compositeLine).productLine;
                if (sellable.sellableId !== compositeOrderItemData.sellableId) {
                    return false;
                }
                const item: OrderItem = this.createOrderItemFromOrderItemData(compositeOrderItemData, sellable.menuItem, sellable, checkQtyAvailable, orderItemData.quantity, runningTotal);
                if (!item) {
                    return false;
                }
                items.push(item);
            }
            return true;
        })) {
            return undefined;
        }
        const orderCompositeItem: OrderCompositeItem = new OrderCompositeItem(undefined, orderItemData.name, menuItem, menuItem.sellable, orderItemData.quantity,
            composite, items, orderItemData.transferRequestId, orderItemData.transferRequestName, orderItemData.deal, orderItemData.proposed);
        if (checkQtyAvailable) {
            orderCompositeItem.accumulateSellableQtyOrdered(qtyToOrder, runningTotal);
        }
        return orderCompositeItem;
    }

    private buildTransferRequests(): void {
        this.transferRequests = this.orderItems
            .map((item: OrderItem) => new TransferRequest(item.transferRequestId, item.transferRequestName))
            .filter((request: TransferRequest, index: number, requests: TransferRequest[]) => requests.findIndex((r: TransferRequest) => r.transferRequestId === request.transferRequestId) === index)
            .map((request: TransferRequest) => {
                request.items = this.orderItems.filter((item: OrderItem) => item.transferRequestId == request.transferRequestId);
                request.subTotal = request.items.reduce((total: number, item: OrderItem) => total + item.initiatedTotal, 0);
                return request;
            })
            .sort((a: TransferRequest, b: TransferRequest) => (a.transferRequestName || "").localeCompare(b.transferRequestName || ""));
        this.totalTransfers = this.transferRequests.filter((request: TransferRequest) => request.transferRequestId).length;
    }

    public async pollTransferRequests(transferTargetId: string, tableNumber: string, beaconId: string): Promise<OrderTransferTargetStatus> {
        try {
            const orderTransferRequests: OrderTransferRequestsData = <OrderTransferRequestsData>EntityData.create(OrderTransferRequestsData)
                .assign(await this.orderTransferRequestPollService.performAction({
                    transferTargetId: transferTargetId,
                    orderType: OrderType.toString(this.orderingContextModel.orderType),
                    tableNumber: tableNumber,
                    beaconId: beaconId
                }));

            // Add items from transfer requests into the order
            const items: OrderItemData[] = [];
            orderTransferRequests.requests.forEach((request: OrderInitiationData) => {
                request.items.forEach((orderItemData: OrderItemData) => {
                    if (!orderItemData.transferRequestId) {
                        orderItemData.transferRequestId = request.requestId;
                        orderItemData.transferRequestName = request.firstName;
                    }
                    if (!this.orderItems.some((existing: OrderItem) => existing.transferRequestId === orderItemData.transferRequestId)) {
                        items.push(orderItemData);
                    }
                });
            });
            if (items.length) {
                this.addOrderItems(this.recreateOrderItems(items, false));
            }
            this.recentOrderService.keepCurrentOrderAlive();

            // Server cannot distinguish whether the group order has been closed due to timeout, or due to order completion.
            if (orderTransferRequests.transferTargetStatus === OrderTransferTargetStatus.CLOSED_TIMEOUT && this.hasOrderPlacement) {
                orderTransferRequests.transferTargetStatus = OrderTransferTargetStatus.CLOSED;
            }

            return orderTransferRequests.transferTargetStatus;
        } catch (error) {
            console.log("ignoring failed poll for transfer target " + transferTargetId);
            return OrderTransferTargetStatus.OPEN;
        }
    }

    public async completeOrder(): Promise<void> {
        this.recentOrderService.deleteCurrentOrder();
        this._canReorderOnLoad = false;
        OrderEvents.orderComplete$.next();
    }

    public get canInitiateOrder(): boolean {
        return (this.hasOrderedItems() || !!this.orderingContextModel.currentStore) && !!this.orderingContextModel.orderType;
    }

    /**
     * Retrieves the available order times for a date.
     * @param expectedCompletion Date that the order is required as a local-time string in the timezone of the store, in the format of yyyy-MM-dd
     */
    public async getAvailableOrderTimes(expectedCompletion: string): Promise<AvailableOrderTimesResponseData> {
        const request: AvailableOrderTimesRequestData = <AvailableOrderTimesRequestData>EntityData.create(AvailableOrderTimesRequestData);
        request.expectedCompletion = expectedCompletion;
        request.orderTypeEnum = this.orderingContextModel.orderType;
        request.allowCarryOverTimes = true;
        request.items = this.orderItems
            .filter((orderItem: OrderItem) => orderItem.quantity > 0)
            .map((orderItem: OrderItem) => this.buildOrderItemData(orderItem));
        return <AvailableOrderTimesResponseData>EntityData.create(AvailableOrderTimesResponseData).assign(await this.availableOrderTimesService.performAction(request));
    }

    public get cannotAllocateTime(): boolean {
        return this.initiatedOrder && this.initiatedOrder.cannotAllocateOrderLaterDateTime;
    }

    public get cannotAllocateOrderTimeMessage(): string {
        if (!this.initiatedOrder) {
            return undefined;
        }
        const reason: string = this.initiatedOrder.unavailabilityReason;
        return reason === "closed" ? (this.orderingContextModel.expectedCompletion ? "woa.message.order-time.storeClosedAtTime" : "woa.message.order-time.storeClosedNow")
            : reason === "closedForDay" ? "woa.message.order-time.storeClosedForTheDay"
                : reason === "closing" ? "woa.message.order-time.storeClosing"
                    : reason === "offline" ? "woa.message.order-time.storeOffline"
                        : reason === "past" ? "woa.message.order-time.timeInPast"
                            : reason === "tooFarAhead" ? "woa.message.order-time.tooFarAhead"
                                : reason === "leadTime" ? "woa.message.order-time.leadTime"
                                    : reason === "integratedDeliveryNotAvailable" ? "woa.message.order-time.integratedDeliveryNotAvailable"
                                        : "woa.message.order-time.cannotAllocate";
    }

    public get invalidOrderTimeMessage(): string {
        return this.initiatedOrder.unavailabilityReason === "offline" ? "woa.error.order.storeOffline"
            : this.orderingContextModel.orderType === OrderType.ORDER_AT_TABLE ? "woa.error.order.storeUnavailableForTableOrders"
                : !this.orderingContextModel.canShowAsapSelectionTime && !this.orderingContextModel.expectedCompletion ? "woa.error.order.noTime"
                    : "woa.error.order.invalidTime";
    }

    public get orderEta(): string {
        return this.orderingContextModel.expectedCompletion || !this.estimatedCompletionLocalDateTime || this.cannotAllocateTime || !this.orderingContextModel.canShowAsapSelectionTime ||
        (this.orderingContextModel.integratedDelivery && (SessionEvents.orderingPhase$.value === OrderingPhase.ORDERING || !this.orderingContextModel.address)) ? undefined
            : DateTime.formatTimeForDisplay(EntityData.parseLocalDateTime(this.estimatedCompletionLocalDateTime));
    }

    private get estimatedCompletionLocalDateTime(): string {
        return this.initiatedOrder ? this.initiatedOrder.estimatedCompletionLocalDateTime : undefined;
    }

    public get orderSaving(): number {
        return this.initiatedOrder.undiscountedOrderTotal ? this.initiatedOrder.undiscountedOrderTotal - this.initiatedOrder.orderTotal : 0;
    }

    public get orderStarted(): boolean {
        return this.dealModel.currentEntitlementStatus === EntitlementStatus.APPLIED || this.hasOrderedItems();
    }

    public get initiationErrorKey(): string {
        return this.initiationError.displayable ? this.initiationError.data.messageKey : "woa.error.order.initiationFailed";
    }

    public get initiationErrorParams(): any {
        return this.initiationError.displayable ? this.initiationError.data.messageParams : {};
    }

    public get totalItems(): number {
        return this.orderItems.reduce((total: number, item: OrderItem) => total + item.quantity, 0);
    }

    public hasOrderedItems(): boolean {
        return this.isOrderValid && this.isOrderValid && this.orderItems.length > 0;
    }

    public get hasOrderPlacement(): boolean {
        return this.orderPlacement && !!this.orderPlacement.requestId;
    }

    public get personalOrderStarted(): boolean {
        return this.dealModel.currentEntitlementStatus === EntitlementStatus.APPLIED || this.hasOrderedItemPersonally(() => true);
    }

    public hasOrderedSellablePersonally(sellableId: number): boolean {
        return this.hasOrderedItemPersonally((item: OrderItem) => item.sellable.sellableId === sellableId);
    }

    public hasOrderedItemPersonally(predicate: (item: OrderItem) => boolean): boolean {
        return this.isOrderValid && this.orderItems.some((item: OrderItem) => !item.transferRequestId && predicate(item));
    }

    public get orderTotal(): number {
        return this.initiatedOrder ? this.initiatedOrder.getOrderOrderTotalWithPaymentSurcharge(PaymentSurchargeData.CREDIT_CARD_TYPE) : 0;
    }

    public get orderTotalPayment(): number {
        return this.orderTotal + (this.tipAmount || 0);
    }

    public set addToOrderAttempted(attempted: boolean) {
        OrderEvents.addToOrderAttempted$.next(attempted);
    }

    public get showMinOrderAheadAmountWarning(): boolean {
        return this.orderingContextModel.orderType === OrderType.ORDER_AHEAD && this.orderingContextModel.currentStore && this.orderingContextModel.currentStore.minPickupOrderAmount &&
            this.initiatedOrder && (this.orderTotal || 0) < this.orderingContextModel.currentStore.minPickupOrderAmount;
    }

    public get showMaxOrderAheadAmountWarning(): boolean {
        return this.orderingContextModel.orderType === OrderType.ORDER_AHEAD && this.orderingContextModel.currentStore && this.orderingContextModel.currentStore.maxPickupOrderAmount &&
            this.initiatedOrder && (this.orderTotal || 0) > this.orderingContextModel.currentStore.maxPickupOrderAmount;
    }

    public get showMinDeliveryAmountWarning(): boolean {
        return this.orderingContextModel.isConfirmedDeliveryOrdering && this.orderingContextModel.currentStore && this.orderingContextModel.currentStore.minDeliveryOrderAmount &&
            this.initiatedOrder && (this.orderTotal || 0) < this.orderingContextModel.currentStore.minDeliveryOrderAmount;
    }

    public get showMaxDeliveryAmountWarning(): boolean {
        return this.orderingContextModel.isConfirmedDeliveryOrdering && this.orderingContextModel.currentStore && this.orderingContextModel.currentStore.maxDeliveryOrderAmount &&
            this.initiatedOrder && (this.orderTotal || 0) > this.orderingContextModel.currentStore.maxDeliveryOrderAmount;
    }

    public get showMinOrderAtTableAmountWarning(): boolean {
        return this.orderingContextModel.isConfirmedTableOrdering && this.orderingContextModel.currentStore && this.orderingContextModel.currentStore.minTableOrderAmount &&
            this.initiatedOrder && (this.orderTotal || 0) < this.orderingContextModel.currentStore.minTableOrderAmount;
    }

    public get showMaxOrderAtTableAmountWarning(): boolean {
        return this.orderingContextModel.isConfirmedTableOrdering && this.orderingContextModel.currentStore && this.orderingContextModel.currentStore.maxTableOrderAmount &&
            this.initiatedOrder && (this.orderTotal || 0) > this.orderingContextModel.currentStore.maxTableOrderAmount;
    }

    public get orderTypeChangedSinceLastInitiation(): boolean {
        const initiatedOrderType: OrderType = this.orderInitiation ? this.orderInitiation.orderTypeEnum : OrderType.NONE;
        const currentOrderType: OrderType = this.orderingContextModel.orderType ? this.orderingContextModel.orderType : OrderType.NONE;
        return initiatedOrderType !== currentOrderType;
    }

    public get canPersistOrderDetails(): boolean {
        return !this.sessionModel.waiterMode && (!this.identityModel.isGuestUser || this.responsiveService.isMobile || this.sessionModel.devOrDemoMode);
    }

    public get paymentRequired(): boolean {
        return this.orderTotalPayment > 0;
    }

    public canKeepAsCurrentOrder(hasGroupOrder: boolean): boolean {
        const keepAsCurrentOrder: boolean = this.canPersistOrderDetails && (this.hasOrderBeenPersisted || this.personalOrderStarted || hasGroupOrder);
        if (keepAsCurrentOrder) {
            this.hasOrderBeenPersisted = true;
        }
        return keepAsCurrentOrder;
    }

    /**
     * Returns false until a menu has been loaded and a store has been selected.
     * Returns true the first time those conditions are met.
     * Returns false subsequently.
     */
    public get canRestoreOnLoad(): boolean {
        if (!(MenuEvents.currentMenu.value && this.orderingContextModel.currentStore)) {
            return false;
        }
        const canRestoreOnLoad: boolean = this._canRestoreOnLoad;
        this._canRestoreOnLoad = false;
        return canRestoreOnLoad;
    }

    /**
     * Returns false until a menu has been loaded and a store and order type have been selected.
     * Returns true the first time those conditions are met.
     * Returns false subsequently.
     */
    public get canReorderOnLoad(): boolean {
        if (!(MenuEvents.currentMenu.value && this.orderingContextModel.currentStore && this.orderingContextModel.orderType)) {
            return false;
        }
        const canReorderOnLoad: boolean = this._canReorderOnLoad;
        this._canReorderOnLoad = false;
        return canReorderOnLoad;
    }

    public set canReorderOnLoad(canReorderOnLoad: boolean) {
        this._canReorderOnLoad = canReorderOnLoad;
    }

    public get canPromptForReorder(): boolean {
        return !this.sessionModel.waiterMode &&
            MenuEvents.currentMenu.value &&
            !MenuEvents.itemOrderPending$.value &&
            this.orderingContextModel.orderType &&
            this.orderingContextModel.currentStore &&
            this.isOrderValid &&
            this.initiationStatus === OrderInitiationStatus.INITIATED &&
            !this.hasOrderedItems() &&
            this.getReorderableOrderItems().length > 0;
    }

    public showQuickPay(requireLogin: boolean = true): boolean {
        if (this.orderingContextModel.fulfilmentDetailsRequired) {
            return false;
        }
        const quickPayType: PaymentType = this.getQuickPayType(requireLogin);
        return quickPayType !== undefined && quickPayType !== PaymentType.NONE;
    }

    public getQuickPayType(requireLogin: boolean = true): PaymentType {
        if (this.sessionModel.waiterMode) {
            return undefined;
        }

        // Cannot complete the order without a successful order initiation
        if (!this.isOrderValid || this.initiationStatus === OrderInitiationStatus.UNINITIATED || this.initiationStatus === OrderInitiationStatus.FAILED) {
            return undefined;
        }

        // Deliveries require the ability to enter instructions
        if (this.orderingContextModel.orderType === OrderType.ORDER_DELIVERY) {
            return undefined;
        }

        // Payment via a group order is always available as quick pay unless this is itself a group order
        if (!GroupOrderEvents.currentGroupOrder$.value && this.initiatedOrder && this.initiatedOrder.availableTransferTargets.length > 0) {
            return PaymentType.GROUP_PAY;
        }

        // As a guest, a login may be required
        if (requireLogin && this.identityModel.isGuestUser && !this.sessionModel.loginOptions.allowGuestOrdering) {
            return undefined;
        }

        // As a member or guest with a stored credit card, we may not have all the required information
        if (this.paymentRequired) {
            if (this.paymentModel.hasSavedCreditCard &&
                (!this.orderingContextModel.requirePhoneNumber || this.orderingContextModel.mobilePhone) &&
                (!this.orderingContextModel.requireEmail || this.orderingContextModel.email) &&
                (!this.orderingContextModel.requireFirstName || this.orderingContextModel.firstName)) {
                return PaymentType.SAVED_CREDIT_CARD;
            }
        }

        // Orders not requiring payment may still require customer details
        else if ((this.orderingContextModel.showPhoneNumber && !this.orderingContextModel.mobilePhone) ||
            (this.orderingContextModel.showEmail && !this.orderingContextModel.email) ||
            (this.orderingContextModel.showName && !this.orderingContextModel.firstName)) {
            return undefined;
        }

        // For a wallet payment where quick pay is available, we assume that any required information will be available through the provider.
        if (this.paymentModel.canMakeApplePayPayment && this.paymentModel.walletPaymentProcessor.quickPayAvailable) {
            return PaymentType.APPLE_PAY;
        }
        if (this.paymentModel.canMakeGooglePayPayment && this.paymentModel.walletPaymentProcessor.quickPayAvailable) {
            return PaymentType.GOOGLE_PAY;
        }
        if (this.orderTotal <= 0) {
            return PaymentType.NONE;
        }
        return undefined;
    }

    public formatPlacedDateTime(placedOrder: PlacedOrderData): string {
        if (!placedOrder) {
            return "";
        }
        return DateTime.formatDateForDisplay(placedOrder.placedDateTime) + " • " + DateTime.formatTimeForDisplay(placedOrder.placedDateTime);
    }

    public formatEstimatedCompletionDateTime(placedOrder: PlacedOrderData): string {
        if (!placedOrder) {
            return "";
        }
        const date: Date = EntityData.parseLocalDateTime(placedOrder.estimatedCompletionLocalDateTime);
        return DateTime.formatDateForDisplay(date) + " • " + DateTime.formatTimeForDisplay(date);
    }

    public formatRequiredDateTime(initiatedOrder: InitiatedOrderData): string {
        if (!initiatedOrder) {
            return "";
        }
        const date: Date = EntityData.parseLocalDateTime(initiatedOrder.orderLaterLocalDateTime);
        return DateTime.formatDateForDisplay(date) + " • " + DateTime.formatTimeForDisplay(date);
    }

    public async editOrderItem(orderItem: DisplayableOrderItem): Promise<void> {
        // noinspection ES6MissingAwait
        this.analyticsService.trackEditItem(orderItem);
        const editedOrderItem: OrderItem = await this.navigationService.createPage("woa-order-item-edit-page", {orderItem: orderItem});
        if (editedOrderItem) {
            // noinspection ES6MissingAwait
            this.analyticsService.trackItemEdited(editedOrderItem);
        }
    }

    public removeDealFromOrder() {
        if (this.isOrderValid) {
            this.orderItems
                .filter((orderItem) => orderItem.deal)
                .forEach((orderItem) => this.removeOrderItemByIdentifier(orderItem.identifier));

            // Save the current order without the entitlement.
            if (this.orderInitiation) {
                this.orderInitiation.entitlementId = undefined;
                this.recentOrderService.saveCurrentOrder(this.orderInitiation, undefined);
            }

            // Save the current order without the entitlement.
            if (this.orderInitiation) {
                this.recentOrderService.saveCurrentOrder(this.orderInitiation, undefined);
            }
        }
    }

    public validateOrderItem(orderItem: OrderItem, showMessage: boolean = true): boolean {
        return orderItem instanceof OrderCompositeItem
            ? this.validateCompositeItem(<OrderCompositeItem>orderItem, showMessage)
            : this.validateSimpleItem(orderItem, undefined, showMessage);
    }

    public validateSimpleItem(orderItem: OrderItem, compositeItem: OrderCompositeItem, showMessage: boolean): boolean {
        if (orderItem.sellable) {
            const missingExtraGroups: MenuExtraGroupData[] = orderItem.getExtraGroupsBelowQuantityMinimum();
            if (missingExtraGroups.length > 0) {
                if (showMessage) {
                    const extraMissing: MenuExtraGroupData = missingExtraGroups[0];
                    // noinspection JSIgnoredPromiseFromCall
                    this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.message.order.minExtraQtyRequired", {
                        minQty: extraMissing.groupQtyMin,
                        item: extraMissing.externalName || extraMissing.name
                    }));
                }
                return false;
            }

            if (orderItem.sellable.decomposedProductLine && (!compositeItem || compositeItem.sellable.decomposedProductLine) && !orderItem.orderedExtras.length) {
                if (showMessage) {
                    // noinspection JSIgnoredPromiseFromCall
                    this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.message.order.extraSelectionRequired", {
                        item: orderItem.sellable.name
                    }));
                }
                return false;
            }

            return true;
        }
        return false;
    }

    private validateCompositeItem(orderItem: OrderCompositeItem, showMessage: boolean): boolean {
        let isValid: boolean = true;
        orderItem.items.find((compositeComponent: OrderItem) => {
            if (compositeComponent instanceof OrderPlgItem) {
                if (!compositeComponent.plg.sellableLines.length) {
                    return false;
                }
                if (!compositeComponent.sellable) {
                    if (showMessage) {
                        const orderPlgItem: OrderPlgItem = <OrderPlgItem>compositeComponent;
                        // noinspection JSIgnoredPromiseFromCall
                        this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.heading.order.plgSelectionRequired", {
                            plgName: orderPlgItem.plg.externalName || orderPlgItem.plg.name
                        }));
                    }
                    isValid = false;
                    return true;
                }
            }
            isValid = this.validateSimpleItem(compositeComponent, orderItem, showMessage);
            return !isValid;
        });
        return isValid;
    }

    private async menuLoaded(menuData: MenuData): Promise<void> {
        if (this.isOrderValid) {
            if (!menuData) {
                return;
            }

            // Remove over-sold items
            let deal: boolean = false;
            let modified: boolean = false;
            [...this.orderItems].forEach((orderItem: OrderItem) => {
                while (this.orderItemAtLimit(orderItem, 0)) {
                    modified = true;
                    if (orderItem.quantity > (orderItem.sellable.minWebOrderQty || 1)) {
                        orderItem.quantity--;
                    } else {
                        this.removeOrderItemByIdentifier(orderItem.identifier);
                        break;
                    }
                    deal = orderItem.deal || deal;
                }
            });
            if (deal) {
                this.dealModel.removeCurrentEntitlement();
            }
            if (modified && SessionEvents.orderingPhase$.value === OrderingPhase.ORDERING && !this.removalOfOversoldItemsReported) {
                await this.notificationService.displayAlert(null, "woa.message.order.itemsNotAvailableNowUnplaced");
            }
            this.removalOfOversoldItemsReported = false;
        }
    }

    /**
     * An item is considered to be at its limit if adding the quantum amount puts it over the limit.
     * Note that this method does not check nested plg items and extras, but it does test nested product lines.
     * Mainly this is because the complexity of sizes, PLGs and extras rules would add a performance penalty, particularly when evaluated across an entire menu.
     *
     * @param menuItem
     * @param quantum The quantity by which this item will be incremented. This reflects the incrementing of ancestor items which already have multiple of this item, or when a quantity of an items is to be added from a deal.
     * @param qtyPending Used when adding a new order item - Specifies the current quantity of the item already selected within the order item.
     * @param excludeOrderItem Used when modifying an order item via a clone to exclude the original version of that item which is still in the order.
     */
    public menuItemAtLimit(menuItem: MenuItem, quantum: number = 1, qtyPending: number = 0, excludeOrderItem?: OrderItem): boolean {
        const qtyToAdd = quantum + qtyPending;
        if (menuItem && menuItem.sellable) {
            const qtyAvailable: number = this.menuModel.sellableQtyAvailable(menuItem.sellable.sellableId);
            if (qtyAvailable != undefined && this.canAddQty(menuItem.sellable.sellableId, qtyToAdd, qtyAvailable, excludeOrderItem)) {
                return true;
            }
        }
        if (this.menuModel.isNormalMenuItem(menuItem)) {
            this.menuModel.initialiseItem((<MenuItemData>menuItem));
            if ((<MenuItemData>menuItem).sellable.composite &&
                (<MenuItemData>menuItem).sellable.composite.prodLineQuantities.some((prodLineQty: MenuProductLineQtyData) => this.menuItemAtLimit(prodLineQty.productLine.menuItem, qtyToAdd, 0, excludeOrderItem))) {
                return true;
            }
        }
        return false;
    }

    /**
     * An item is considered to be at its limit if adding the quantum amount puts it over the limit.
     * @param sellableId
     * @param quantum The quantity by which this item will be incremented. This reflects the incrementing of ancestor items which already have multiple of this item, or when a quantity of an items is to be added from a deal.
     * @param qtyPending Specifies the current quantity of the item already selected within the order item.
     * @param excludeOrderItem Used when modifying an order item via a clone to exclude the original version of that item which is still in the order.
     */
    public sellableAtLimit(sellableId: number, quantum: number = 1, qtyPending: number = 0, excludeOrderItem?: OrderItem): boolean {
        const qtyToAdd = quantum + qtyPending;
        if (sellableId) {
            const qtyAvailable: number = this.menuModel.sellableQtyAvailable(sellableId);
            if (qtyAvailable != undefined && this.canAddQty(sellableId, qtyToAdd, qtyAvailable, excludeOrderItem)) {
                return true;
            }
        }
        return false;
    }

    /**
     * An item is considered to be at its limit if adding the quantum amount puts it over the limit.
     * This method also checks nested components and extras which are currently on the item.
     *
     * @param orderItem
     * @param quantum The quantity by which this item will be incremented. This reflects the incrementing of ancestor items which already have multiple of this item, or when a quantity of an items is to be added from a deal.
     * @param qtyPending Used in conjunction with excludeOrderItem when modifying an order item via a clone - Specifies the current quantity of the item being edited.
     * @param excludeOrderItem Used when modifying an order item via a clone to exclude the original version of that item which is still in the order.
     */
    public orderItemAtLimit(orderItem: OrderItem, quantum: number = 1, qtyPending: number = 0, excludeOrderItem?: OrderItem): boolean {
        const qtyToAdd = quantum + qtyPending;
        if (orderItem.sellable) {
            const qtyAvailable: number = this.menuModel.sellableQtyAvailable(orderItem.sellable.sellableId);
            if (qtyAvailable != undefined && this.canAddQty(orderItem.menuItem ? orderItem.menuItem.sellable.sellableId : orderItem.sellable.sellableId, qtyToAdd, qtyAvailable, excludeOrderItem)) {
                return true;
            }
        }
        if (orderItem instanceof OrderCompositeItem) {
            if (orderItem.items.some((compositeItem: OrderItem) => this.orderItemAtLimit(compositeItem, qtyToAdd * compositeItem.quantity, 0, excludeOrderItem))) {
                return true;
            }
        } else if (orderItem.orderedExtras.some((extraItem: OrderExtraItem) => this.sellableAtLimit(
            extraItem.extraProduct.menuItem ? extraItem.extraProduct.menuItem.sellable.sellableId : extraItem.extraProduct.productLine.sellableId,
            qtyToAdd * extraItem.quantity, 0, excludeOrderItem))) {
            return true;
        }
        return false;
    }

    public canAddQty(sellableId: number, qtyToAdd: number, qtyAvailable: number, excludeOrderItem?: OrderItem): boolean {
        return this.isOrderValid && this.sellableQtyOrdered(sellableId, excludeOrderItem) + qtyToAdd > qtyAvailable;
    }

    public getReorderableOrderItems(): OrderItem[] {
        const recentOrders: RecentOrderData[] = this.recentOrderService.recentOrders;
        if (recentOrders.length == 0) {
            return [];
        }

        // only consider orders that are no older than 8 hours
        const cutOffDate: Date = new Date();
        cutOffDate.setHours(cutOffDate.getHours() - OrderModel.REORDER_ROUND_CUTOFF_HOURS);
        if (recentOrders[0].initiatedOrder.initiatedDateTime.getTime() < cutOffDate.getTime()) {
            return [];
        }

        return this.recreateOrderItems(recentOrders[0].orderInitiation.items
            .filter((orderItemData: OrderItemData) =>
                orderItemData.reorderableInApp &&
                (!orderItemData.transferRequestId || this.orderingContextModel.orderType === OrderType.ORDER_AT_TABLE)));
    }

    /**
     * Removes order items that are unavailable according to the specified request.
     * @returns true is there is nothing to remove, or if the unavailable items are removed.
     * @returns false if confirm is specified as true and the user rejects the removal.
     * @param request
     */
    public async removeUnavailableOrderItems(request: UnavailableOrderItemRemovalRequest): Promise<boolean> {
        if (!this.isOrderValid) {
            return true;
        }

        const unavailableItems: OrderItem[] = this.orderItems.filter((orderItem: OrderItem) => {
            if (request.schedules) {
                if (!this.menuModel.itemIsAvailableOnSchedules(orderItem.menuItem)) {
                    return true;
                }
            } else if (request.orderType) {
                if (!this.menuModel.itemIsAvailableForOrderType(orderItem.menuItem, request.orderType)) {
                    return true;
                }
            } else {
                return false;
            }
        });
        if (!unavailableItems.length) {
            return true;
        }

        if (request.confirm) {
            const confirmationMessage: string = request.schedules ? "woa.message.order.itemsUnavailableAtTime"
                : request.orderType === OrderType.ORDER_AHEAD ? "woa.message.order.itemsUnavailableOrderAhead"
                    : request.orderType === OrderType.ORDER_DELIVERY ? "woa.message.order.itemsUnavailableOrderDelivery"
                        : "woa.message.order.itemsUnavailableOrderAtTable";

            let itemNames: string = unavailableItems.map((item) => item.menuItem.name).join(", ");
            if (itemNames.length > 70) {
                itemNames = itemNames.substring(0, 70) + "...";
            }
            this.loadingService.dismissSpinner();
            if (!await this.notificationService.displayConfirmation("woa.heading.order.confirmRemoveUnavailableItems", undefined,
                confirmationMessage, "woa.button.common.cancel", "woa.button.common.proceed", true, {items: itemNames})) {
                return false;
            }
        }

        let deal: boolean = false;
        unavailableItems.forEach((orderItem: OrderItem) => {
            console.log("Removing item from order: " + orderItem.sellable.name);
            this.removeOrderItemByIdentifier(orderItem.identifier);
            deal = orderItem.deal || deal;
        });
        if (deal) {
            this.dealModel.removeCurrentEntitlement();
        }

        return true;
    }

    private buildOrderItemData(orderItem: OrderItem): OrderItemData {
        const orderItemData: OrderItemData = <OrderItemData>EntityData.create(OrderItemData);
        orderItemData.identifier = orderItem.identifier;
        orderItemData.sellableId = orderItem.sellable.sellableId;
        orderItemData.name = orderItem.name;
        orderItemData.quantity = orderItem.quantity;
        orderItemData.unitPrice = orderItem.unitPrice;
        if (orderItem.menuItem) {
            orderItemData.courseId = orderItem.menuItem.courseId;
        }
        orderItemData.applyMinimumAge = orderItem.sellable.applyMinimumAge;
        orderItemData.reorderableInApp = orderItem.sellable.reorderableInApp;
        orderItemData.transferRequestId = orderItem.transferRequestId;
        orderItemData.transferRequestName = orderItem.transferRequestName;
        orderItemData.deal = orderItem.deal;
        orderItemData.proposed = orderItem.proposed;
        orderItemData.extras = orderItem.orderedExtras.map((orderExtraItem: OrderExtraItem) => {
            const extraItemData: ExtraItemData = <ExtraItemData>EntityData.create(ExtraItemData);
            extraItemData.sellableId = orderExtraItem.extraProduct.productLine.sellableId;
            extraItemData.name = orderExtraItem.name;
            extraItemData.quantity = orderExtraItem.quantity;
            extraItemData.unitPrice = orderExtraItem.unitPrice;
            extraItemData.isGroup = orderExtraItem.isGroup;
            extraItemData.isVisible = orderExtraItem.isVisible;
            extraItemData.isNested = orderExtraItem.isNested;
            extraItemData.groupName = orderExtraItem.groupName;
            return extraItemData;
        });
        if (orderItem instanceof OrderCompositeItem) {
            orderItemData.compositeOrderItems = orderItem.items.map((component: OrderItem) => this.buildOrderItemData(component));
        }
        return orderItemData;
    }

    /**
     * Registers the order with the server for pricing, availability, etc
     * @param reason Logged to console
     * @param initiateIfNewOrder Indicates that the order should be initiated if a new order is created. This is needed if the order has products or other info at the time of creating the order.
     */
    public requestOrderInitiation(reason: string, initiateIfNewOrder: boolean = false): void {
        console.log(Date.now(), "order initiation requested:", reason);
        if (OrderEvents.orderSubmissionStatus$.value === OrderSubmissionStatus.SUBMITTING) {
            console.log("suppressing order initiation - order placement has commenced");
            return;
        }

        this.orderErrorStates = new OrderErrorStates();

        if (this.orderingContextModel.orderType === OrderType.ORDER_AT_TABLE && this.orderingContextModel.expectedCompletion) {
            if (this.orderingContextModel.currentStore && (this.orderingContextModel.currentStore.offline || this.orderingContextModel.currentStore.closed)) {
                this.orderErrorStates.orderWithInvalidTimeAttempted = true;
            } else {
                this.orderErrorStates.tableOrderWithTimeAttempted = true;
            }
        }

        OrderEvents.orderInitiationRequestReceived$.next(reason);

        if (!this.isOrderValid) {
            this.startNewOrder("order initiation");
            if (!initiateIfNewOrder) {
                return;
            }
        }
        if (!this.canInitiateOrder) {
            console.log("insufficient information to initiate order");
            return;
        }

        // Once an initiation has been requested, we treat the order as being in INITIATING status.
        // This is important for untilOrderInitiationComplete() to work properly.
        OrderEvents.orderInitiationStatus$.next(OrderInitiationStatus.INITIATING);

        this.orderInitiationRequested$.next();
    }

    private async initiateOrder(): Promise<void> {
        if (OrderEvents.orderSubmissionStatus$.value === OrderSubmissionStatus.SUBMITTING) {
            console.log("suppressing order initiation - order placement has commenced");
            return;
        }

        console.log("initiating order");
        OrderEvents.orderInitiationStatus$.next(OrderInitiationStatus.INITIATING);

        // The "checkIntegratedDeliveryTime" flag on order initiation is used to limit the frequency of integrated delivery address lookups.
        // Quick pay is not enabled for deliveries since an opportunity must be required to enter or amend delivery instructions.
        // Therefore, delivery "checkout" always occurs via the payment page.
        const checkIntegratedDeliveryTime: boolean = SessionEvents.orderingPhase$.value === OrderingPhase.PAYMENT;

        // The "forPayment" flag on order initiation is used to control whether payment details are expected to be returned with the initiated order (if required for the payment platform).
        // For now, we require this only when on the payment page - this may change depending on Tyro's ability to handle quick pay flows.
        // Stripe doesn't require this, but the flag is ignored in that case.
        const forPayment: boolean = SessionEvents.orderingPhase$.value === OrderingPhase.PAYMENT;
        const paymentCustomerDetails: PaymentCustomerDetails = forPayment ? this.customerDetailsService.loadPaymentCustomer() : undefined;

        try {
            // Retry loop
            let attempts: number = 0;
            while (true) {
                attempts++;

                // Build the order initiation data
                this.orderInitiation = <OrderInitiationData>EntityData.create(OrderInitiationData);
                this.orderInitiation.requestId = UUID.UUID();
                this.orderInitiation.orderTypeEnum = this.orderingContextModel.orderType;
                this.orderInitiation.tableNumber = this.orderingContextModel.currentStore.tableNumber;
                this.orderInitiation.beaconId = this.sessionModel.beaconId;
                if (GroupOrderEvents.currentGroupOrder$.value) {
                    this.orderInitiation.orderTransferTargetId = GroupOrderEvents.currentGroupOrder$.value.id;
                    this.orderInitiation.transferTargetName = GroupOrderEvents.currentGroupOrder$.value.name;
                }
                this.orderInitiation.platformCustomerId = paymentCustomerDetails ? paymentCustomerDetails.paymentCustomerId : undefined;
                this.orderInitiation.forPayment = forPayment;
                this.orderInitiation.checkIntegratedDeliveryTime = checkIntegratedDeliveryTime;

                // Address is required for delivery order initiation.
                if (checkIntegratedDeliveryTime) {
                    this.orderInitiation.address = this.orderingContextModel.address && this.orderingContextModel.address.street ? this.orderingContextModel.address : this.identityModel.currentIdentity.homeAddress;
                }

                this.orderInitiation.firstName = this.orderingContextModel.trimmedFirstName || "NOT ASSIGNED";
                this.orderInitiation.lastName = this.orderingContextModel.trimmedLastName || "NOT ASSIGNED";
                if (this.dealModel.currentEntitlement) {
                    this.orderInitiation.entitlementId = this.dealModel.currentEntitlement.id;
                    this.orderInitiation.integratedDealConfigId = this.dealModel.currentEntitlement.integratedDealConfigId;
                    this.orderInitiation.integratedDealProviderName = this.dealModel.currentEntitlement.providerName;
                }
                if (this.orderingContextModel.orderType !== OrderType.ORDER_AT_TABLE) {
                    this.orderInitiation.expectedCompletion = this.orderingContextModel.expectedCompletionAsString;
                }
                this.orderInitiation.items = this.orderItems
                    .filter((orderItem: OrderItem) => orderItem.quantity > 0)
                    .map((orderItem: OrderItem) => this.buildOrderItemData(orderItem));
                this.orderInitiation.tipAmount = this.tipAmount;

                // Persist the current order
                if (this.canKeepAsCurrentOrder(GroupOrderEvents.groupOrderStatus$.value === OrderTransferTargetStatus.OPEN)) {
                    this.recentOrderService.saveCurrentOrder(this.orderInitiation, DealEvents.currentEntitlement$.value);
                }

                // Perform the initiation
                const expectedInitiationSequence: number = ++this.initiationSequence;
                this.initiationStatus = OrderInitiationStatus.INITIATING;
                console.log("requesting order initiation with sequence nr " + expectedInitiationSequence);
                try {
                    const result: any = await this.orderInitiationService.performAction(this.orderInitiation);
                    if (expectedInitiationSequence !== this.initiationSequence) {
                        console.log("ignoring response to superseded initiation request with sequence nr " + expectedInitiationSequence);
                        return;
                    }
                    // @ts-ignore
                    if (this.initiationStatus === OrderInitiationStatus.UNINITIATED) {
                        return;
                    }

                    console.log("assigning response for order initiation with sequence nr " + expectedInitiationSequence);
                    this.initiatedOrder = <InitiatedOrderData>EntityData.create(InitiatedOrderData).assign(result);
                    this.orderingContextModel.currentStore.completionTime = this.initiatedOrder.store.completionTime;
                    this.orderingContextModel.currentStore.currentWaitTimeMinutes = this.initiatedOrder.store.currentWaitTimeMinutes;
                    this.initiatedOrder.store = this.orderingContextModel.currentStore;
                    this.initiatedOrder.tipAmount = this.tipAmount;
                    this.initiatedOrder.initiatedOrderItems.forEach((initiatedOrderItem: InitiatedOrderItemData) => {
                        const orderItem: OrderItem = this.orderItems.find((orderItem: OrderItem) => orderItem.identifier === initiatedOrderItem.identifier);
                        if (orderItem) {
                            orderItem.justOrdered = orderItem.initiatedTotal == undefined;
                            orderItem.initiatedTotal = initiatedOrderItem.total;
                            orderItem.initiatedExtrasTotal = initiatedOrderItem.extrasTotal;
                            if (orderItem instanceof OrderCompositeItem) {
                                orderItem.compositeOrderItems.forEach((compositeOrderItem: DisplayableOrderItem) =>
                                    compositeOrderItem.initiatedExtrasTotal = initiatedOrderItem.compositeOrderItems.find((initiatedCompositeOrderItem: InitiatedOrderItemData) =>
                                        initiatedCompositeOrderItem.identifier === compositeOrderItem.identifier).extrasTotal);
                            }
                        }

                        const initiationOrderItem: OrderItemData = this.orderInitiation.items.find((initiationOrderItem: OrderItemData) => initiationOrderItem.identifier === initiatedOrderItem.identifier);
                        if (initiationOrderItem) {
                            initiationOrderItem.initiatedTotal = initiatedOrderItem.total;
                            initiationOrderItem.initiatedExtrasTotal = initiatedOrderItem.extrasTotal;
                            initiationOrderItem.compositeOrderItems.forEach((compositeOrderItem: OrderItemData) =>
                                compositeOrderItem.initiatedExtrasTotal = initiatedOrderItem.compositeOrderItems.find((initiatedCompositeOrderItem: InitiatedOrderItemData) =>
                                    initiatedCompositeOrderItem.identifier === compositeOrderItem.identifier).extrasTotal);
                        }
                    });

                    this.buildTransferRequests();
                    this.initiationStatus = OrderInitiationStatus.INITIATED;

                    // Initiation successful - terminate retry loop.
                    break;
                } catch (error) {
                    if (expectedInitiationSequence !== this.initiationSequence) {
                        console.log("ignoring response to superseded initiation request with sequence nr " + expectedInitiationSequence);
                        return;
                    }
                    // @ts-ignore
                    if (this.initiationStatus === OrderInitiationStatus.UNINITIATED) {
                        return;
                    }

                    this.initiationStatus = OrderInitiationStatus.FAILED;
                    this.initiationError = error;

                    // Log initiation failure to console
                    let message: string = "AppOrderModel.initiateOrder - order initiation failed";
                    console.log(message, error);

                    // Retry idiopathic response error
                    if (error instanceof DataClientErrorMessage && error.causeType === "Response" && error.cause.status === 0) {
                        if (attempts < OrderModel.ORDER_INITIATION_ATTEMPTS_LIMIT) {
                            console.log("retrying initiation after " + attempts + " failed attempt(s)");
                            continue;
                        }
                        console.log("not retrying initiation after " + attempts + " failed attempt(s)");
                        message += " " + attempts + " time(s)";
                    }

                    // Log initiation failure to server
                    this.notificationService.logErrorToServer(message, this.orderInitiation, error);
                    break;
                }
            }

            // Obtain payment provider and/or key from order initiation if present
            this.paymentModel.applyPaymentDetails(this.initiatedOrder.paymentDetails);

            this.notifyOrderInitiationStatus();
            this.orderInitiatedTime = new Date().getTime();
            console.log("order initiation complete");
        } catch (error) {
            // initiateOrder() does not reject, but rather resolves with the order in error state - this is here for the unexpected

            console.error(error);
            // noinspection ES6MissingAwait
            this.analyticsService.trackAnomaly("initiateOrder failed", error);
            this.notificationService.logErrorToServer("AppOrderModel.initiateOrder - order initiation failed unexpectedly", this.orderInitiation, error);

            OrderEvents.orderInitiationStatus$.next(OrderInitiationStatus.FAILED);
            this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.order.initiationFailedFatal"));
        }
    }

    /**
     * Allows the caller to wait for order initiation to complete.
     */
    public untilOrderInitiationComplete(): Promise<void> {
        return new Promise<void>((resolve) => {
            const sub: Subscription = OrderEvents.orderInitiationStatus$.delay(0).subscribe((status: OrderInitiationStatus) => {
                if (status !== OrderInitiationStatus.INITIATING) {
                    sub.unsubscribe();
                    resolve();
                }
            });
        });
    }

    public notifyOrderInitiationStatus(initiationStatus: OrderInitiationStatus = this.initiationStatus): void {
        this.initiationStatus = initiationStatus;
        OrderEvents.orderInitiationStatus$.next(this.initiationStatus);
    }

    public validateOrderForPayment(paymentType: PaymentType, applyErrorStates: boolean): boolean {
        const orderErrorStates: OrderErrorStates = new OrderErrorStates();
        orderErrorStates.emptyOrderAttempted = !this.hasOrderedItems();
        orderErrorStates.orderWithInvalidTimeAttempted = (this.orderingContextModel.orderType !== OrderType.ORDER_AT_TABLE && !this.orderingContextModel.canShowAsapSelectionTime && !this.orderingContextModel.expectedCompletion) || this.initiatedOrder.cannotAllocateOrderLaterDateTime;
        orderErrorStates.minMaxOrderAmountAttempted =
            paymentType !== PaymentType.GROUP_PAY && paymentType !== PaymentType.NONE &&
            (this.showMinOrderAheadAmountWarning || this.showMaxOrderAheadAmountWarning ||
                this.showMinDeliveryAmountWarning || this.showMaxDeliveryAmountWarning ||
                this.showMinOrderAtTableAmountWarning || this.showMaxOrderAtTableAmountWarning);
        if (applyErrorStates) {
            this.orderErrorStates = orderErrorStates;
        }
        return !orderErrorStates.hasErrors;
    }

    /**
     * Build a recent order data object from the order
     */
    buildRecentOrderData(): RecentOrderData {
        const recentOrder: RecentOrderData = <RecentOrderData>EntityData.create(RecentOrderData);
        recentOrder.orderInitiation = this.orderInitiation;
        recentOrder.initiatedOrder = this.initiatedOrder;
        recentOrder.orderPlacement = this.orderPlacement;
        recentOrder.placedOrder = this.placedOrder;
        return recentOrder;
    }

    /**
     * Performs all order placement processing, including validation, obtaining payment details, error handling, and retry for both errors and 2FA.
     *
     * @param paymentType
     * @param transferTarget Only for PaymentType.GROUP_PAY
     * @param paymentCard Only for PaymentType.NEW_CREDIT_CARD
     * @param validate Perform page-specific validations
     * @param cardValidationFailed Handle card validation errors
     */
    public async placeOrder(paymentType: PaymentType,
                            transferTarget: OrderTransferTargetReferenceData,
                            paymentCard?: PaymentCard,
                            validate?: () => boolean,
                            cardValidationFailed?: () => void): Promise<boolean> {
        if (paymentType == undefined) {
            if (!this.sessionModel.waiterMode) {
                return false;
            }
            paymentType = PaymentType.NEW_CREDIT_CARD;
        }

        // Prevent double-submission
        if (OrderEvents.orderSubmissionStatus$.value === OrderSubmissionStatus.SUBMITTING) {
            console.log("Already started placing order");
            if (Date.now() - this.orderPlacementStarted > OrderModel.ORDER_PLACEMENT_DELAY_TRIGGER_SECONDS * 1000) {
                // noinspection ES6MissingAwait
                this.analyticsService.trackDelay("order placement preparation");
            }
            return false;
        }
        if (OrderEvents.orderSubmissionStatus$.value === OrderSubmissionStatus.COMPLETE) {
            console.log("Order already placed");
            // noinspection ES6MissingAwait
            this.analyticsService.trackAnomaly("placement attempt after order placed");
            return false;
        }

        // Sanity check initiation
        if (this.orderInitiation.requestId !== this.initiatedOrder.requestId) {
            console.log("Order initiation outstanding");
            // noinspection ES6MissingAwait
            this.analyticsService.trackAnomaly("placement attempt with initiation outstanding");
            OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.INCOMPLETE);
            return false;
        }

        // Sanity check payment type
        if ((paymentType === PaymentType.APPLE_PAY || paymentType === PaymentType.GOOGLE_PAY) && !this.paymentModel.canMakeWalletPayment) {
            OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.INCOMPLETE);
            return false;
        }
        if (paymentType === PaymentType.SAVED_CREDIT_CARD && !this.paymentModel.hasSavedCreditCard) {
            OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.INCOMPLETE);
            return false;
        }

        // Sanity check table number
        if (this.orderInitiation.orderTypeEnum === OrderType.ORDER_AT_TABLE && !this.orderInitiation.tableNumber) {
            console.log("No table number for table order");
            // noinspection ES6MissingAwait
            this.analyticsService.trackAnomaly("placement attempt without table number");
            // noinspection ES6MissingAwait
            this.notificationService.displayAlert(undefined, this.sessionModel.noTableMessage);
            OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.INCOMPLETE);
            return false;
        }

        // Order time validation
        if (this.initiatedOrder && this.initiatedOrder.orderLaterDateTime != undefined && this.initiatedOrder.cannotAllocateOrderLaterDateTime) {
            // noinspection ES6MissingAwait
            this.notificationService.displayAlert(undefined, "woa.error.order.pleaseSelectAnotherTime");
            OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.INCOMPLETE);
            return false;
        }

        // Perform page-specific validations (if any)
        if (validate && !validate()) {
            OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.INCOMPLETE);
            return false;
        }

        // Warn if placing order for an empty group order
        if (GroupOrderEvents.currentGroupOrder$.value && !this.totalTransfers &&
            !await this.notificationService.displayConfirmation("woa.heading.payment.confirmEmptyGroupOrder", undefined,
                "woa.message.payment.confirmEmptyGroupOrder",
                "woa.button.payment.abortEmptyGroupOrder",
                "woa.button.payment.continueEmptyGroupOrder",
                true, undefined)) {
            OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.INCOMPLETE);
            // noinspection ES6MissingAwait
            this.analyticsService.trackPlaceOrderAbandoned("emptyGroupOrderPrompt");
            return false;
        }

        // Prompt for additional information as required
        if (!(this.paymentModel.paymentProvider === IntegratedPaymentProvider.TYRO && this.sessionModel.orderingPhase === OrderingPhase.PAYMENT) &&
            this.paymentModel.canTip(paymentType) && !await this.orderingContextModel.promptForTip()) {
            OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.INCOMPLETE);
            // noinspection ES6MissingAwait
            this.analyticsService.trackPlaceOrderAbandoned("tipPrompt");
            return false;
        }
        if (paymentType === PaymentType.GROUP_PAY &&
            !await this.orderingContextModel.promptForFirstName(transferTarget ? new PossessivePipe().transform(transferTarget.name) : undefined)) {
            OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.INCOMPLETE);
            // noinspection ES6MissingAwait
            this.analyticsService.trackPlaceOrderAbandoned("namePrompt");
            return false;
        }
        if ((paymentType === PaymentType.SAVED_CREDIT_CARD || paymentType === PaymentType.NEW_CREDIT_CARD || paymentType === PaymentType.POINTS || paymentType === PaymentType.NONE) &&
            !await this.orderingContextModel.confirmPhoneNumberForSms()) {
            OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.INCOMPLETE);
            // noinspection ES6MissingAwait
            this.analyticsService.trackPlaceOrderAbandoned("mobilePhonePrompt");
            return false;
        }

        OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.SUBMITTING);
        this.orderPlacementStarted = Date.now();

        const orderPlacedEvent: OrderPlacedEvent = new OrderPlacedEvent(this.orderingContextModel.orderType, this.identityModel.isGuestUser,
            this.orderTotalPayment, paymentType, this.totalItems,
            this.paymentModel.canMakeWalletPayment, SessionEvents.orderingPhase$.value !== OrderingPhase.PAYMENT,
            this.orderingContextModel.currentStore.tableNumber);
        // noinspection ES6MissingAwait
        this.analyticsService.trackPlaceOrderStarted(orderPlacedEvent);

        if (paymentType === PaymentType.GROUP_PAY) {
            if (!transferTarget) {
                transferTarget = this.initiatedOrder.availableTransferTargets.length > 1 ? this.initiatedOrder.availableTransferTargets[0] : undefined;
                if (!transferTarget) {
                    // noinspection ES6MissingAwait
                    this.analyticsService.trackAnomaly("group pay attempt with transfer target");
                    OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.INCOMPLETE);
                    return false;
                }
            }
        } else {
            transferTarget = undefined;
        }

        try {
            let paymentAuthorisation: PaymentAuthorisationData = undefined;

            // Retry loop for 3DS and prompted retry
            let retryRequired: boolean = true;
            while (retryRequired) {
                retryRequired = false;
                if (!await this.loadingService.performWithSpinner(async () => {

                    // Obtain payment details
                    paymentAuthorisation = await this.paymentModel.authorisePayment(paymentType, paymentCard, paymentAuthorisation, cardValidationFailed);
                    if (!paymentAuthorisation) {
                        return false;
                    }

                    const orderCustomerDetails: CustomerDetails = new CustomerDetails();
                    const nameTrusted: boolean = this.sessionModel.waiterMode ||
                        (!!this.orderingContextModel.trimmedFirstName && this.orderingContextModel.firstNameTrusted) ||
                        (!!this.orderingContextModel.trimmedLastName && this.orderingContextModel.lastNameTrusted);
                    const emailTrusted: boolean = this.sessionModel.waiterMode ||
                        (!!this.orderingContextModel.email && this.orderingContextModel.emailTrusted);
                    const mobilePhoneTrusted: boolean = this.sessionModel.waiterMode ||
                        (!!this.orderingContextModel.mobilePhone && this.orderingContextModel.mobilePhoneTrusted);
                    const marketingOptIn: boolean = this.sessionModel.waiterMode ||
                        (!!this.orderingContextModel.marketingOptIn && nameTrusted && emailTrusted && mobilePhoneTrusted);

                    // Untrusted fields in the ordering context are not used for the order unless they are shown.
                    if (nameTrusted || this.orderingContextModel.showName) {
                        orderCustomerDetails.firstName = this.orderingContextModel.trimmedFirstName;
                        orderCustomerDetails.lastName = this.orderingContextModel.trimmedLastName;
                    }
                    if (emailTrusted || this.orderingContextModel.showEmail) {
                        orderCustomerDetails.email = this.orderingContextModel.email;
                    }
                    if (mobilePhoneTrusted || this.orderingContextModel.showPhoneNumber) {
                        orderCustomerDetails.mobilePhone = this.orderingContextModel.mobilePhone;
                    }

                    // Update trusted customer contact details in browser storage.
                    if (!this.sessionModel.waiterMode) {
                        const savedCustomerDetails: CustomerDetails = this.customerDetailsService.loadCustomer();
                        if (nameTrusted) {
                            savedCustomerDetails.firstName = this.orderingContextModel.trimmedFirstName;
                            savedCustomerDetails.lastName = this.orderingContextModel.trimmedLastName;
                        }
                        if (emailTrusted) {
                            savedCustomerDetails.email = this.orderingContextModel.email;
                        }
                        if (mobilePhoneTrusted) {
                            savedCustomerDetails.mobilePhone = this.orderingContextModel.mobilePhone;
                        }
                        if (marketingOptIn) {
                            savedCustomerDetails.marketingOptInDecided = true;
                        }
                        this.customerDetailsService.saveCustomer(savedCustomerDetails);
                    }

                    // Place the order
                    try {

                        // Populate order placement details
                        if (paymentType === PaymentType.GROUP_PAY && transferTarget) {
                            this.orderInitiation.firstName = this.orderingContextModel.firstName;
                            this.orderInitiation.orderTransferTargetId = GroupOrderEvents.currentGroupOrder$.value ? GroupOrderEvents.currentGroupOrder$.value.id : undefined;
                            this.orderInitiation.transferTargetId = transferTarget.id;
                            this.orderInitiation.transferTargetName = transferTarget.name;
                            this.orderPlacement = undefined;
                        } else {
                            if (!this.initiatedOrder) {
                                this.orderPlacement = undefined;
                                throw new OrderError(OrderErrorCode.ORDER_NOT_INITIATED, "Cannot build order placement - order has not been initiated");
                            }
                            if (!this.initiatedOrder.orderToken) {
                                throw new OrderError(OrderErrorCode.ORDER_TOKEN_MISSING, "Cannot build order placement - no order token available");
                            }

                            this.orderPlacement = <OrderPlacementData>EntityData.create(OrderPlacementData);
                            this.orderPlacement.requestId = this.initiatedOrder.requestId;
                            this.orderPlacement.orderToken = this.initiatedOrder.orderToken;
                            this.orderPlacement.paymentAuthorisation = paymentAuthorisation;
                            this.orderPlacement.firstName = orderCustomerDetails.firstName;
                            this.orderPlacement.lastName = orderCustomerDetails.lastName;
                            this.orderPlacement.email = orderCustomerDetails.email;
                            this.orderPlacement.mobilePhone = orderCustomerDetails.mobilePhone;
                            this.orderPlacement.nameTrusted = nameTrusted;
                            this.orderPlacement.emailTrusted = emailTrusted;
                            this.orderPlacement.mobilePhoneTrusted = mobilePhoneTrusted;
                            this.orderPlacement.marketingOptIn = marketingOptIn;
                            this.orderPlacement.customerNotes = this.orderingContextModel.customerNotes;
                            this.orderPlacement.address = this.orderingContextModel.address;
                            this.orderPlacement.deliveryInstructions = this.orderingContextModel.deliveryInstructions;
                            this.orderPlacement.saveAddressForMember = this.orderingContextModel.saveAddressForMember && this.identityModel.currentMember.allowProfileEdit;
                            this.orderPlacement.appSessionId = this.sessionModel.appSessionId;
                            this.orderPlacement.scanId = this.sessionModel.scanId;
                            this.orderPlacement.appUserId = this.appUserService.getAppUserId();
                            this.orderPlacement.orderTransferTargetId = GroupOrderEvents.currentGroupOrder$.value ? GroupOrderEvents.currentGroupOrder$.value.id : undefined;
                            this.orderPlacement.checkAvailableQtys = this.orderingContextModel.isStoreOpenAtCompletionTime;
                            this.orderPlacement.orderItemSchedules = this.orderItems.map((orderItem: OrderItem) => {
                                const orderItemSchedules: OrderItemSchedulesData = <OrderItemSchedulesData>EntityData.create(OrderItemSchedulesData);
                                orderItemSchedules.itemIdentifier = orderItem.identifier;
                                orderItemSchedules.mergedSchedules = orderItem.menuItem.mergedSchedules;
                                return orderItemSchedules;
                            });
                        }

                        // Persist the recent order details
                        if (this.canPersistOrderDetails) {
                            const recentPendingOrder: RecentOrderData = this.buildRecentOrderData();
                            recentPendingOrder.pending = true;
                            this.recentOrderService.addRecentOrder(recentPendingOrder);
                        }

                        console.log("placing order");
                        try {
                            this.placedOrder = <PlacedOrderData>EntityData.create(PlacedOrderData).assign((await (paymentType === PaymentType.GROUP_PAY
                                ? this.orderTransferService.performAction(this.orderInitiation)
                                : this.orderPlacementService.performAction(this.orderPlacement))));

                            // update the app user id in case the server used another id when placing the order
                            if (this.placedOrder.appUserId && this.placedOrder.appUserId !== this.appUserService.getAppUserId()) {
                                this.appUserService.updateAppUserId(this.placedOrder.appUserId);
                            }

                            if (this.canPersistOrderDetails && this.placedOrder.status === OrderPlacementStatus.ACCEPTED) {
                                this.recentOrderService.addRecentOrder(this.buildRecentOrderData());
                                this.recentOrderService.deleteCurrentOrder();
                            }
                        } catch (error) {
                            this.orderPlacement = undefined;
                            this.recentOrderService.deletePendingOrder(this.initiatedOrder.requestId);
                            throw error;
                        }

                        // Send the order-placed event here if the placement is complete.
                        // If it is still pending (foreground integrated order processing), we pass the event on the order completion page to send (or not) depending on the eventual result.
                        if (this.placedOrder.status === OrderPlacementStatus.ACCEPTED) {
                            // noinspection ES6MissingAwait
                            this.analyticsService.trackOrderPlaced(orderPlacedEvent);
                        }

                        this.paymentModel.completePayment(paymentAuthorisation);

                        console.log("order placement complete");

                        this.loadingService.dismissSpinner();
                        this.orderPlacementFinished = Date.now();
                        OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.COMPLETE);

                        // noinspection ES6MissingAwait
                        this.navigationService.createPage("woa-order-confirmation-page", {orderPlacedEvent: orderPlacedEvent}, undefined, this.placedOrder.status !== OrderPlacementStatus.PENDING);
                    } catch (error) {
                        console.error(error);
                        this.loadingService.dismissSpinner();
                        let paymentAuthorisationFailed: boolean = false;

                        // Handle expected errors including 3DS, and retry where possible.

                        // Order not initiated or no order token - cause unknown - possible race condition.
                        if (error instanceof OrderError && (error.code === OrderErrorCode.ORDER_NOT_INITIATED || error.code === OrderErrorCode.ORDER_TOKEN_MISSING)) {
                            // noinspection ES6MissingAwait
                            this.analyticsService.trackPlaceOrderFailed("invalidOrderInitiationState");
                            if (await this.notificationService.displayConfirmation("woa.heading.order.invalidInitiationState", undefined, "woa.error.order.invalidInitiationState",
                                "woa.button.order.return", "woa.button.common.retry", false, undefined, true)) {
                                retryRequired = true;
                            } else {
                                OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.ORDER_INVALID);
                            }
                        }

                        // Duplicate order attempt - log and ignore (no reporting to user)
                        else if (error.status === 409) {
                            // noinspection ES6MissingAwait
                            this.analyticsService.trackPlaceOrderFailed("duplicateAttemptIgnored");
                            OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.INCOMPLETE);
                        }

                        // 3DS handling required
                        else if (error.data && error.data["conditionName"] === "cardPaymentRequiresAction") {
                            // noinspection ES6MissingAwait
                            this.analyticsService.track3DSStart(this.orderTotalPayment);

                            const dataServerConditionNotification: DataServerConditionNotification = error.data;
                            const action: any = dataServerConditionNotification.conditionParams["action"];
                            try {
                                const threeDSResult: ThreeDSResult = await this.paymentModel.handle3DS(action, paymentAuthorisation);
                                if (threeDSResult.success) {
                                    retryRequired = true;
                                } else {
                                    console.error(threeDSResult.error);
                                    // noinspection ES6MissingAwait
                                    this.analyticsService.track3DSFailedOnClient(this.orderTotalPayment);
                                    // noinspection ES6MissingAwait
                                    this.analyticsService.trackPlaceOrderFailed("paymentAuthorisationFailed");
                                    await this.notificationService.displayAlert(undefined, "woa.message.order.paymentAuthorisationFailed");
                                    OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.INCOMPLETE);
                                }
                            } catch (error) {
                                console.error(error);
                                // noinspection ES6MissingAwait
                                this.analyticsService.track3DSFailedOnClient(this.orderTotalPayment);
                                // noinspection ES6MissingAwait
                                this.analyticsService.trackPlaceOrderFailed("3DS processing error");
                                await this.notificationService.displayAlert(undefined, "woa.message.order.paymentAuthorisationFailed");
                                OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.INCOMPLETE);
                            }
                        }

                        // Failure due to outstanding group order participants
                        else if (error.data && error.data["conditionName"] === "outstandingParticipantRequests") {
                            // noinspection ES6MissingAwait
                            this.analyticsService.trackPlaceOrderFailed("outstandingParticipantRequests");
                            await this.notificationService.displayAlert("woa.heading.order.outstandingParticipants", "woa.message.order.outstandingParticipants");
                            OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.ORDER_INVALID);
                        }

                        // Failure due to insufficient integrated loyalty rewards balance
                        else if (error.data && error.data["conditionName"] === "insufficientIntegratedLoyaltyRewards") {
                            // noinspection ES6MissingAwait
                            this.analyticsService.trackPlaceOrderFailed("insufficientIntegratedLoyaltyRewards");
                            await this.notificationService.displayAlert("woa.heading.order.insufficientIntegratedLoyaltyRewards", "woa.message.order.insufficientIntegratedLoyaltyRewards");
                            OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.ORDER_INVALID);
                        }

                        // Failure due to product unavailability due to schedules
                        else if (error.data && error.data["conditionName"] === "notAvailableSchedules") {
                            // noinspection ES6MissingAwait
                            this.analyticsService.trackPlaceOrderFailed("itemsNotAvailable");

                            // Attempt to recreate the group order that will have been closed by the failed order placement, rather than confusing the user with a prompt.
                            // noinspection ES6MissingAwait
                            this._groupOrderModel.restoreGroupOrder();

                            const dataServerConditionNotification: DataServerConditionNotification = error.data;
                            const orderRequested: Date = dataServerConditionNotification.conditionParams["pickupRequested"] ? new Date(dataServerConditionNotification.conditionParams["pickupRequested"]) : null;
                            const preparationExpected: Date = EntityData.parseLocalDateTime(dataServerConditionNotification.conditionParams["preparationExpected"]);
                            const requestedTime: string = DateTime.formatTimeForDisplay(orderRequested);
                            const expectedTime: string = DateTime.formatTimeForDisplay(preparationExpected);
                            const msg: string = this.initiatedOrder.orderLaterDateTime == undefined ?
                                (orderRequested ? "woa.message.order.itemsNotAvailableWhenRequested" : "woa.message.order.itemsNotAvailableNow")
                                : orderRequested && preparationExpected > orderRequested ? "woa.message.order.itemsNotAvailableRevisedSchedule"
                                    : "woa.message.order.itemsNotAvailableActiveSchedule";

                            await this.notificationService.displayAlert("woa.heading.order.itemsNotAvailableActiveSchedule", msg, {
                                requestedTime: requestedTime,
                                expectedTime: expectedTime
                            });
                            this.orderingContextModel.expectedCompletion = preparationExpected;
                            await this.orderingContextModel.refreshActiveSchedules();
                            OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.ORDER_INVALID);
                        }

                        // Failure due to limited product availability due to countdown
                        else if (error.data && error.data["conditionName"] === "productLimitReached") {
                            // noinspection ES6MissingAwait
                            this.analyticsService.trackPlaceOrderFailed("productLimitReached");

                            // Attempt to recreate the group order that will have been closed by the failed order placement, rather than confusing the user with a prompt.
                            // noinspection ES6MissingAwait
                            this._groupOrderModel.restoreGroupOrder();

                            // const dataServerConditionNotification: DataServerConditionNotification = error.data;
                            // const oversoldSellableIds: number[] = dataServerConditionNotification.conditionParams["oversoldSellableIds"];

                            await this.notificationService.displayAlert("woa.heading.order.itemsOversold", "woa.message.order.itemsOversold");
                            this.removalOfOversoldItemsReported = true;
                            // noinspection ES6MissingAwait
                            this.menuModel.loadMenu("product limit reached");
                            OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.ORDER_INVALID);
                        }

                        // Handle all other failure causes
                        else if (!error.displayable) {
                            // noinspection ES6MissingAwait
                            this.analyticsService.trackPlaceOrderFailed("submitFailed");
                            if (await this.notificationService.displayConfirmation("woa.heading.order.orderSubmissionFailed", undefined, "woa.error.order.orderSubmissionFailed",
                                "woa.button.order.cancelOrder", "woa.button.common.retry", false, undefined, true)) {

                                // Retry using the same token (if any), unless payment details have been saved against a new customer,
                                // in which case the previous token may have been assigned to a since-deleted customer.
                                if (paymentAuthorisation.storePaymentDetails) {
                                    paymentAuthorisation.token = undefined;
                                }
                                retryRequired = true;
                            } else {
                                OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.ORDER_CANCELLED);
                            }
                        } else {
                            // noinspection ES6MissingAwait
                            this.analyticsService.trackPlaceOrderFailed(error.data.messageKey);
                            if (error.data && error.data.messageKey === "server.exception.web-order.intentCreationFailed") {
                                paymentAuthorisationFailed = true;
                                await this.notificationService.displayAlert(undefined, "woa.message.order.paymentAuthorisationFailed");
                            } else if (error.data && error.data.messageKey === "server.exception.web-order.intentCreationCardDetailsFailed") {
                                paymentAuthorisationFailed = true;
                                await this.notificationService.displayAlert("woa.message.order.paymentAuthorisationFailed", error.data.messageKey, error.data.messageParams);
                            } else {
                                await this.notificationService.displayAlert("woa.error.order.submitFailed", error.data.messageKey, error.data.messageParams);
                            }
                            OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.INCOMPLETE);
                        }

                        // Log errors if we are not about to retry
                        if (!retryRequired) {
                            this.notificationService.logErrorToServer("AppOrderModel.placeOrder - order placement failed", this.orderPlacement, error);

                            // threeDsPaymentIntent is only set for a 3DS retry. Track the completion status of the 3DS retry.
                            if (paymentAuthorisation.isThreeDS) {
                                if (paymentAuthorisationFailed) {
                                    // noinspection ES6MissingAwait
                                    this.analyticsService.track3DSFailedOnServer(this.orderTotalPayment);
                                } else {
                                    // A different server side issue not 3DS related
                                    // noinspection ES6MissingAwait
                                    this.analyticsService.track3DSSucceeded(this.orderTotalPayment);
                                }
                            }
                        }
                    }

                    // Indicate order placement succeeded
                    return true;
                }, 60000)) {
                    return false;
                }
            }
            return true;
        } catch (error) {
            console.log("placeOrder", error);
            OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.INCOMPLETE);
            // noinspection ES6MissingAwait
            this.analyticsService.trackAnomaly("placeOrder failed", error);
            return false;
        } finally {
            OrderEvents.orderSubmissionStatus$.next(undefined);
        }
    }

    public async getOrderPlacementStatus(keepAsRecentOrder: boolean): Promise<OrderPlacementStatusData> {
        if (!this.placedOrder) {
            return EntityData.create(OrderPlacementStatusData);
        }
        const orderPlacementStatus: OrderPlacementStatusData = <OrderPlacementStatusData>EntityData.create(OrderPlacementStatusData)
            .assign(await this.orderPlacementStatusService.performAction({
                requestId: this.placedOrder.requestId,
                lookupCode: this.placedOrder.lookupCode
            }));
        this.placedOrder.status = orderPlacementStatus.status;

        if (keepAsRecentOrder && orderPlacementStatus.status === OrderPlacementStatus.ACCEPTED) {
            this.recentOrderService.addRecentOrder(this.buildRecentOrderData());
            this.recentOrderService.deleteCurrentOrder();
        }
        return orderPlacementStatus;
    }

    public async performPostscript(email?: string, mobilePhone?: string, postCode?: string, marketingOptIn?: boolean): Promise<void> {
        const orderPostscript: OrderPostscriptData = <OrderPostscriptData>EntityData.create(OrderPostscriptData);
        orderPostscript.requestId = this.initiatedOrder.requestId;
        orderPostscript.appUserId = this.appUserService.getAppUserId();
        orderPostscript.email = email;
        orderPostscript.mobilePhone = mobilePhone;
        orderPostscript.postCode = postCode;
        orderPostscript.marketingOptIn = marketingOptIn;
        return this.orderPostscriptService.performAction(orderPostscript);
    }

    /**
     * Sends a list of pending orders to the server and gets back all orders that have actually been placed.
     */
    public async verifyPendingOrders(): Promise<void> {
        if (this.verifyingPendingOrders) {
            return;
        }
        this.verifyingPendingOrders = true;
        this.verifiedOrders = [];
        try {
            const recentOrders: RecentOrderData[] = this.recentOrderService.recentPendingOrders;
            if (recentOrders.length) {
                const pendingOrders: PendingOrdersData = <PendingOrdersData>EntityData.create(PendingOrdersData);
                pendingOrders.pendingOrders = recentOrders.map((recentOrder: RecentOrderData) => {
                    const pendingOrder: PendingOrderData = <PendingOrderData>EntityData.create(PendingOrderData);
                    pendingOrder.requestId = recentOrder.initiatedOrder.requestId;
                    pendingOrder.webTerminalId = recentOrder.initiatedOrder.store.webTerminalId;
                    return pendingOrder;
                });

                const placedOrders: PlacedOrdersData = <PlacedOrdersData>EntityData.create(PlacedOrdersData).assign(await this.pendingOrdersService.performAction(pendingOrders));

                recentOrders.forEach((recentOrder: RecentOrderData) => {
                    const placedOrder: PlacedOrderData = placedOrders.placedOrders.find((value) => value.requestId === recentOrder.initiatedOrder.requestId);
                    if (placedOrder) {
                        recentOrder.placedOrder = placedOrder;
                        recentOrder.pending = false;
                        this.recentOrderService.addRecentOrder(recentOrder);
                        this.verifiedOrders.push(recentOrder);
                    } else {
                        this.recentOrderService.deletePendingOrder(recentOrder.initiatedOrder.requestId);
                    }
                });
            }
        } catch (error) {
            console.error(error);
            throw error;
        } finally {
            this.verifyingPendingOrders = false;
        }
    }

    public async notifyVerifiedOrders(): Promise<void> {
        while (this.verifiedOrders.length) {
            const verifiedOrder: RecentOrderData = this.verifiedOrders.shift();
            await this.notificationService.displayAlert("woa.heading.order.orderPlaced", verifiedOrder.orderPlacement.firstName ? "woa.message.order.orderPlaced.withName" : "woa.message.order.orderPlaced.anonymous", {
                name: verifiedOrder.orderPlacement.firstName,
                deliveryIdentifier: verifiedOrder.placedOrder.receiptId
            });
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/order/order.model.ts